import { useEffect, useMemo, useState } from "react"

/**
 * Manages a single component reference link with per-field toggles.
 *
 * `componentRef` — ID of the linked component library task (one per task).
 * `linkedFields` — which fields inherit from the component,
 *   e.g. { classification: true, duration: true, frequency: true, skillSet: true }
 *
 * @param {string|null} initialRef
 * @param {object} initialLinkedFields
 */
export function useComponentRef(initialRef = null, initialLinkedFields = {}) {
    const normalizedLinkedFields = useMemo(buildNormalizedLinkedFields, [initialLinkedFields])
    const [componentRef, setComponentRef] = useState(initialRef ?? null)
    const [linkedFields, setLinkedFields] = useState(normalizedLinkedFields)

    useEffect(syncInitialState, [initialRef, normalizedLinkedFields])

    return { componentRef, linkedFields, setComponentRef: updateRef, enableField, disableField, setAllFields }

    function syncInitialState() {
        setComponentRef(initialRef ?? null)
        setLinkedFields(normalizedLinkedFields)
    }

    function updateRef(id) {
        setComponentRef(id ?? null)
    }

    function enableField(field) {
        setLinkedFields((prev) => ({ ...prev, [field]: true }))
    }

    function disableField(field) {
        setLinkedFields((prev) => normalizeLinkedFields({ ...prev, [field]: false }))
    }

    function setAllFields(fields) {
        setLinkedFields(normalizeLinkedFields(fields))
    }

    function buildNormalizedLinkedFields() {
        return normalizeLinkedFields(initialLinkedFields)
    }
}

export function hasLinkedFields(linkedFields = {}) {
    return Object.values(linkedFields).some(Boolean)
}

export function normalizeLinkedFields(linkedFields = {}) {
    if (!linkedFields || typeof linkedFields !== "object") return {}

    const normalized = {}
    for (const [key, value] of Object.entries(linkedFields)) {
        if (value) normalized[key] = true
    }
    return normalized
}
